import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { performanceReviewService } from '../../services/api';
import { FiArrowLeft, FiEdit2 } from 'react-icons/fi';
import { format, parseISO } from 'date-fns';
import './ReviewDetail.css';

export const ReviewDetail = () => {
  const { reviewId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isAcknowledging, setIsAcknowledging] = useState(false);

  useEffect(() => {
    fetchReview();
  }, [reviewId]);

  const fetchReview = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await performanceReviewService.getById(reviewId);
      setReview(response.data);
    } catch (err) {
      setError('Failed to load review');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    setIsAcknowledging(true);
    setError(null);
    setSuccess(null);

    try {
      await performanceReviewService.acknowledge(reviewId);
      setSuccess('Review acknowledged successfully');
      fetchReview();
    } catch (err) {
      setError('Failed to acknowledge review');
      console.error(err);
    } finally {
      setIsAcknowledging(false);
    }
  };

  const getStarRating = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={i <= rating ? 'star filled' : 'star'}>
          ★
        </span>
      );
    }
    return stars;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'status-draft';
      case 'submitted':
        return 'status-submitted';
      case 'acknowledged':
        return 'status-acknowledged';
      default:
        return 'status-draft';
    }
  };

  const isOwnReview = review?.userId === user?.id;
  const isReviewer = review?.reviewerId === user?.id;
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  const canAcknowledge = isOwnReview && review?.status === 'submitted';
  const canEdit = (isReviewer || isAdmin) && review?.status === 'draft';
  const canSubmit = (isReviewer || isAdmin) && review?.status === 'draft';

  if (loading) {
    return <div className="loading-detail">Loading review...</div>;
  }

  if (!review) {
    return <div className="error-detail">Review not found</div>;
  }

  return (
    <div className="review-detail-container">
      <div className="detail-header">
        <button
          className="btn-back"
          onClick={() => navigate('/performance-review')}
        >
          <FiArrowLeft /> Back
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="review-card">
        <div className="card-header">
          <div className="header-info">
            <h1>{review.employee ? `${review.employee.firstName} ${review.employee.lastName}` : 'Employee'}</h1>
            <p className="period">
              {format(parseISO(review.reviewPeriodStart), 'MMMM yyyy')} -{' '}
              {format(parseISO(review.reviewPeriodEnd), 'MMMM yyyy')}
            </p>
          </div>
          <div className="header-meta">
            <div className="meta-item">
              <span className="label">Reviewer:</span>
              <span className="value">{review.reviewer ? `${review.reviewer.firstName} ${review.reviewer.lastName}` : 'Manager'}</span>
            </div>
            <div className="meta-item">
              <span className="label">Date:</span>
              <span className="value">
                {format(parseISO(review.createdAt), 'MMM d, yyyy')}
              </span>
            </div>
            <span className={`status-badge ${getStatusColor(review.status)}`}>
              {review.status}
            </span>
          </div>
        </div>

        <div className="rating-section">
          <h3>Overall Rating</h3>
          <div className="rating-display">
            <div className="rating-stars">
              {getStarRating(review.rating || 0)}
            </div>
            <span className="rating-value">{review.rating} / 5</span>
          </div>
        </div>

        <div className="sections-container">
          {review.goals && (
            <section className="review-section">
              <h3>Goals</h3>
              <p className="section-content">{review.goals}</p>
            </section>
          )}

          {review.achievements && (
            <section className="review-section">
              <h3>Achievements</h3>
              <p className="section-content">{review.achievements}</p>
            </section>
          )}

          {review.areasOfImprovement && (
            <section className="review-section">
              <h3>Areas of Improvement</h3>
              <p className="section-content">{review.areasOfImprovement}</p>
            </section>
          )}

          {review.comments && (
            <section className="review-section">
              <h3>Comments</h3>
              <p className="section-content">{review.comments}</p>
            </section>
          )}
        </div>

        <div className="actions">
          {canEdit && (
            <button
              className="btn btn-secondary"
              onClick={() => navigate(`/performance-review/${reviewId}/edit`)}
            >
              <FiEdit2 /> Edit
            </button>
          )}

          {canSubmit && (
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/performance-review/${reviewId}/edit`)}
            >
              Submit Review
            </button>
          )}

          {canAcknowledge && (
            <button
              className="btn btn-primary"
              onClick={handleAcknowledge}
              disabled={isAcknowledging}
            >
              {isAcknowledging ? 'Acknowledging...' : 'Acknowledge Review'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewDetail;
